/* global HTMLAuxUploaderElement */
import { useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentLocalTime } from "common/date-utils";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { noteModuleName, notesApi, useGetNotesQuery, useDeleteDocumentMutation, useUploadDocumentMutation } from "store/notes/api";
import { DocumentUploadState } from "store/notes/types";
import { UndoActionCreators, removeDocument, addPendingDocument } from "store/notes";
import { markDocumentAsCancelled } from "store/notes/bus-subscriptons";
import { toUpper, isEqual } from "lodash";
import { PickerDateTimeFormat } from "components/ux/ins-date-pickers/types";
import { Region } from "common/regions";
import { notesAuxDatePickerDefaultLocalized, notesAuxDatePickerDefaultNonLocalized, notesAuxDatePickerLocalizedByRegion, notesAuxDatePickerShortLocalizedByRegion } from "./constants";
import { AuxNotificationStyleEnum, AuxNotificationToastTypeEnum, AuxUploaderConstants } from "@blk/aladdin-web-components";
import { useLazyGetDownloadsQuery } from "components/clickthrough/store/api";
import { addNotification } from "store/notification";
const getUndoableState = (state) => state?.[noteModuleName];
const getPresentState = (state) => getUndoableState(state)?.present;
export const useGetNotes = (props) => {
    const dispatch = useDispatch();
    const { isSuccess } = useGetNotesQuery({
        additionalEntityId: props.additionalEntityId,
        entityId: props.id,
        entityType: toUpper(props.dashboardTypeDisplay)
    });
    useEffect(() => {
        if (isSuccess) {
            dispatch(UndoActionCreators.clearHistory());
        }
    }, [isSuccess, dispatch]);
    return useSelector((state) => getPresentState(state));
};
export const useCurrentNoteKey = () => useSelector((state) => getPresentState(state)?.currentNoteKey);
export const useEditorCurrentNote = (key) => useSelector((state) => (key ? getPresentState(state)?.editors?.[key] ?? null : null));
export const useEditor = () => useSelector((state) => getPresentState(state)?.editors);
export const useGetSelectedNote = () => {
    const key = useCurrentNoteKey();
    return key ? { key } : undefined;
};
export const useGetSelectedNoteId = () => {
    const key = useCurrentNoteKey();
    return useSelector((state) => (key ? getPresentState(state)?.editors?.[key]?.id : undefined));
};
export const useCurrentEditedNote = () => {
    const key = useCurrentNoteKey();
    return [key, useSelector((state) => (key ? getPresentState(state)?.editors?.[key] ?? null : null))];
};
export const useNotesLoading = () => useSelector((state) => getPresentState(state)?.isLoading || false);
export const useNotesError = () => useSelector((state) => getPresentState(state)?.error || null);
export const useGetAllNotes = () => useSelector((state) => {
    const editors = getPresentState(state)?.editors;
    return editors ? Object.values(editors) : [];
});
const normalizeHtmlContent = (html) => {
    if (!html) {
        return "";
    }
    return html
        .replace(/<p><br><\/p>$/gu, "")
        .replace(/<p><\/p>$/gu, "")
        .trim();
};
export const useIsNoteModified = (noteKey) => useSelector((state) => {
    if (!noteKey) {
        return false;
    }
    const presentState = getPresentState(state);
    const currentNote = presentState?.editors?.[noteKey];
    const savedNote = presentState?.savedEditors?.[noteKey];
    if (!savedNote || !currentNote) {
        return Boolean(currentNote);
    }
    const normalizedCurrent = {
        ...currentNote,
        content: normalizeHtmlContent(currentNote.content),
        documents: undefined
    };
    const normalizedSaved = {
        ...savedNote,
        content: normalizeHtmlContent(savedNote.content),
        documents: undefined
    };
    return !isEqual(normalizedCurrent, normalizedSaved);
});
const resolveRegion = (region) => (Object.values(Region).includes(region) ? region : Region.enUS);
export const useNotesAuxDatePickerConfig = (input) => useMemo(() => {
    const resolvedRegion = resolveRegion(input.region);
    let resolvedFormatProps = notesAuxDatePickerDefaultNonLocalized;
    if (input.format === PickerDateTimeFormat.Localized) {
        resolvedFormatProps = notesAuxDatePickerLocalizedByRegion[resolvedRegion] ?? notesAuxDatePickerDefaultLocalized;
    }
    else if (input.format === PickerDateTimeFormat.ShortLocalized) {
        resolvedFormatProps = notesAuxDatePickerShortLocalizedByRegion[resolvedRegion] ?? notesAuxDatePickerDefaultNonLocalized;
    }
    return {
        auxDatePickerProps: {
            ...resolvedFormatProps,
            skipValidation: true
        }
    };
}, [input.format, input.region]);
export const useAllUnsavedNotes = () => useSelector((state) => {
    const presentState = getPresentState(state);
    const editors = presentState?.editors;
    const savedEditors = presentState?.savedEditors;
    if (!editors) {
        return [];
    }
    const unsavedNotes = [];
    Object.keys(editors).forEach((key) => {
        const currentNote = editors[key];
        const savedNote = savedEditors?.[key];
        if (!savedNote && currentNote) {
            unsavedNotes.push(currentNote);
            return;
        }
        if (!currentNote) {
            return;
        }
        const normalizedCurrent = {
            ...currentNote,
            content: normalizeHtmlContent(currentNote.content),
            documents: undefined
        };
        const normalizedSaved = savedNote
            ? {
                ...savedNote,
                content: normalizeHtmlContent(savedNote.content),
                documents: undefined
            }
            : null;
        if (!isEqual(normalizedCurrent, normalizedSaved)) {
            unsavedNotes.push(currentNote);
        }
    });
    return unsavedNotes;
});
export const useHasUnsavedChanges = () => {
    const unsavedNotes = useAllUnsavedNotes();
    return unsavedNotes.length > 0;
};
export const useHasActiveUploads = () => useSelector((state) => {
    const presentState = getPresentState(state);
    const editors = presentState?.editors;
    if (!editors) {
        return false;
    }
    return Object.values(editors).some((note) => note.documents.some((doc) => doc.status === DocumentUploadState.FILE_UPLOAD_STATUS_UNSPECIFIED ||
        doc.status === DocumentUploadState.FILE_UPLOAD_STATUS_UPLOAD_PENDING ||
        doc.status === DocumentUploadState.FILE_UPLOAD_STATUS_SCAN_IN_PROGRESS));
});
export const useCancelActiveUploads = () => {
    const dispatch = useDispatch();
    const editors = useSelector((state) => getPresentState(state)?.editors);
    return useCallback(() => {
        if (!editors) {
            return;
        }
        const activeUploads = [];
        Object.values(editors).forEach((note) => {
            note.documents.forEach((doc) => {
                const isActiveUpload = doc.status === DocumentUploadState.FILE_UPLOAD_STATUS_UNSPECIFIED ||
                    doc.status === DocumentUploadState.FILE_UPLOAD_STATUS_UPLOAD_PENDING ||
                    doc.status === DocumentUploadState.FILE_UPLOAD_STATUS_SCAN_IN_PROGRESS;
                if (isActiveUpload && doc.uploadId) {
                    activeUploads.push({ documentId: doc.id, uploadId: doc.uploadId });
                }
            });
        });
        activeUploads.forEach(({ documentId, uploadId }) => {
            markDocumentAsCancelled(documentId);
            dispatch(removeDocument(documentId));
            const cancelUrl = `${window.REACT_APP_NOTES_BACKEND_BASE_URL || ""}/documents/upload/cancel`;
            const cancelData = JSON.stringify({ uploadId });
            if (navigator.sendBeacon) {
                const blob = new Blob([cancelData], { type: "application/json" });
                navigator.sendBeacon(cancelUrl, blob);
            }
        });
    }, [dispatch, editors]);
};
const FILE_SIZE_BASE = 1024;
const formatFileSize = (bytes) => {
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    let len = bytes;
    let order = 0;
    while (len >= FILE_SIZE_BASE && order < sizes.length - 1) {
        order += 1;
        len /= FILE_SIZE_BASE;
    }
    return `${len.toFixed(2)} ${sizes[order]}`;
};
export const useFileOperations = (noteId, noteKey) => {
    const uploaderRef = useRef(null);
    const [uploadDocument] = useUploadDocumentMutation();
    const [deleteDocument] = useDeleteDocumentMutation();
    const [getDownload] = useLazyGetDownloadsQuery();
    const dispatch = useDispatch();
    const triggerFileSelect = useCallback(() => {
        const input = uploaderRef.current?.shadowRoot?.querySelector(`.${AuxUploaderConstants.CONTAINER_INPUT_SIMPLE}`);
        input?.click();
    }, []);
    const handleFileUpload = useCallback(async (event) => {
        const files = event.detail?.newValue;
        if (!files?.length || !noteId || !noteKey) {
            return;
        }
        const uploadTasks = files.map(async (file) => {
            const crypto = window.crypto || window.msCrypto;
            const array = new Uint32Array(1);
            crypto.getRandomValues(array);
            const tempId = -Date.now() - (array[0] % 1000);
            dispatch(addPendingDocument({
                document: {
                    fileName: file.name,
                    fileSize: formatFileSize(file.size),
                    id: tempId,
                    noteId,
                    resourceId: "",
                    status: DocumentUploadState.FILE_UPLOAD_STATUS_UNSPECIFIED
                },
                noteKey
            }));
            try {
                await uploadDocument({ documentId: tempId, file, noteId, noteKey }).unwrap();
            }
            catch {
                dispatch(removeDocument(tempId));
                dispatch(notesApi.util.invalidateTags(["Notes"]));
                dispatch(addNotification({
                    id: Date.now().toString(),
                    message: language.en.notes.documents.uploadFailed(file.name),
                    notificationStyle: AuxNotificationStyleEnum.ERROR,
                    showMessageCount: false,
                    toastTimeout: 5000,
                    toastType: AuxNotificationToastTypeEnum.PERSISTENT
                }));
            }
        });
        await Promise.all(uploadTasks);
        uploaderRef.current?.clear();
        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.NotesUploadDocument, properties: { LocalTimestamp: getCurrentLocalTime() } }, { fileCount: String(files.length), noteId: String(noteId) });
    }, [noteId, noteKey, uploadDocument, dispatch]);
    const handleDownload = useCallback(async (doc) => {
        const result = await getDownload({
            isCompressFile: false,
            isPreviewDownload: false,
            resourceId: doc.resourceId
        });
        if (result.data?.contentUrl) {
            window.open(result.data.contentUrl, "_blank");
        }
        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.NotesDownloadDocument, properties: { LocalTimestamp: getCurrentLocalTime() } }, { documentId: String(doc.id), noteId: String(noteId) });
    }, [getDownload, noteId]);
    const handleDelete = useCallback(async (doc) => {
        if (doc.status !== DocumentUploadState.FILE_UPLOAD_STATUS_READY) {
            markDocumentAsCancelled(doc.id);
            dispatch(removeDocument(doc.id));
        }
        await deleteDocument({ documentId: doc.id });
        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.NotesDeleteDocument, properties: { LocalTimestamp: getCurrentLocalTime() } }, { documentId: String(doc.id), noteId: String(noteId) });
    }, [deleteDocument, dispatch, noteId]);
    return { handleDelete, handleDownload, handleFileUpload, triggerFileSelect, uploaderRef };
};
